import { useMemo } from 'react'

import PageSuggestionForm from '@/components/forms/page-suggestions'
import RichText from '@/components/ui/rich-text'
import PrintWrapper from '@/components/print-wrapper'
import { slugify } from '@/utils/strings'
import { isPrint } from '@/utils/print'
import HelpParent from './help-parent'
import Routes from '@/constants/routes'

import {
  TypeComponentHubSpotForm,
  TypeHeaderBanner,
  TypeHelpArticle,
  TypeHelpTopNav,
  TypeSectionFooter,
} from '@/types/ctf'
import { ThemeProvider } from '@/components/theme-provider'

type HelpArticleProps = {
  entry: TypeHelpArticle<'WITHOUT_UNRESOLVABLE_LINKS'>
  helpNav: TypeHelpTopNav<'WITHOUT_UNRESOLVABLE_LINKS'>[]
  pageSuggestionsForm: TypeComponentHubSpotForm<'WITHOUT_UNRESOLVABLE_LINKS'>
  headerBanners: TypeHeaderBanner<'WITHOUT_UNRESOLVABLE_LINKS'>[]
  footerNav: TypeSectionFooter<'WITHOUT_UNRESOLVABLE_LINKS'>
  newsletterForm: TypeComponentHubSpotForm<'WITHOUT_UNRESOLVABLE_LINKS'>
  app: any
  seo: any
  toc: any
}

export default function HelpArticle(props: HelpArticleProps) {
  const {
    entry: {
      fields: { title, body, slug, seo, customBreadcrumbs, hideFeedbackButton },
    },
    pageSuggestionsForm,
    footerNav,
    newsletterForm,
    app,
    headerBanners,
    toc,
  } = props

  // find path
  const helpNavInitial = useMemo(() => {
    const result: { index: number; title: string }[] = []

    const findPath = (items: any[], targetSlug: string, path: typeof result = []): typeof result | null => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const { title, slug, items: children } = item.fields || {}

        const newPath = [...path, { index: i, title }]
        if (slug === targetSlug) {
          return newPath
        }
        if (children && children.length > 0) {
          const found = findPath(children, targetSlug, newPath)
          if (found) return found
        }
      }
      return null
    }

    const foundPath = props.helpNav
      .map((nav, i) =>
        findPath(nav?.fields.items || [], props.entry.fields.slug || '', [{ index: i, title: nav.fields.title }])
      )
      .find(Boolean)

    if (foundPath) {
      result.push(...foundPath.slice(0, -1)) // Exclude the last item which is the current article
    }
    return result
  }, [props.entry.fields.slug, props.helpNav])

  if (isPrint())
    return (
      <PrintWrapper
        title={title}
        section={'Help Center'}
        pageUrl={` ${Routes.hostname}/help/${slug}/`}
        pageType="help"
        pageBreadcrumb={helpNavInitial}
      >
        <>
          <h1 id={slugify(title)}>{title}</h1>
          <RichText body={body} />
        </>
      </PrintWrapper>
    )

  return (
    <ThemeProvider>
      <HelpParent
        {...{
          seo: { ...props.seo, ...seo },
          title,
          slug,
          helpNav: props.helpNav,
          helpNavInitial,
          footerNav,
          newsletterForm,
          searchInHeader: true,
          app,
          headerBanners,
          toc,
          displayPdfButton: true,
          customBreadcrumbs,
        }}
      >
        <h1 id={slugify(title)} className="mt-4">
          {title}
        </h1>
        <RichText body={body} />
        {!hideFeedbackButton && (
          <div className="mt-24">
            <PageSuggestionForm form={pageSuggestionsForm} />
          </div>
        )}
      </HelpParent>
    </ThemeProvider>
  )
}
